import * as THREE from 'three';
import type { CommandTool, Field, GameState } from '../state/types';
import type { SceneContext } from '../scene/setup';

const MENU_ID = 'field-action-menu';

const FIELD_BTN_CLASS =
  'px-3 py-1.5 rounded-lg text-[10px] font-bold tracking-wider border transition duration-200 bg-emerald-500/20 hover:bg-emerald-500/35 text-emerald-300 border-emerald-500/40 hover:border-emerald-500/60';
const DRONE_BTN_CLASS = FIELD_BTN_CLASS;
const TRUCK_BTN_CLASS = FIELD_BTN_CLASS;

export type FieldActionFn = (fieldId: number, unit: Exclude<CommandTool, 'auto'>) => void;

let menuEl: HTMLDivElement | null = null;
const worldPos = new THREE.Vector3();
let onAction: FieldActionFn | null = null;

function ensureMenu(container: HTMLElement): HTMLDivElement {
  if (menuEl) return menuEl;

  menuEl = document.createElement('div');
  menuEl.id = MENU_ID;
  menuEl.className =
    'absolute z-30 pointer-events-auto flex flex-col gap-1.5 p-2 rounded-xl glass-panel shadow-2xl border border-slate-700/60';
  menuEl.style.display = 'none';
  menuEl.style.transform = 'translate(-50%, -100%)';

  const title = document.createElement('div');
  title.className =
    'field-action-title text-[10px] font-bold text-slate-300 uppercase tracking-wider px-0.5';
  menuEl.appendChild(title);

  const btnRow = document.createElement('div');
  btnRow.className = 'flex gap-1.5';

  const droneBtn = document.createElement('button');
  droneBtn.type = 'button';
  droneBtn.dataset.action = 'drone';
  droneBtn.className = DRONE_BTN_CLASS;
  droneBtn.textContent = '🚁 Felmérés';

  const truckBtn = document.createElement('button');
  truckBtn.type = 'button';
  truckBtn.dataset.action = 'truck';
  truckBtn.className = TRUCK_BTN_CLASS;
  truckBtn.textContent = '🚗 Permetezés';

  btnRow.appendChild(droneBtn);
  btnRow.appendChild(truckBtn);
  menuEl.appendChild(btnRow);

  menuEl.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!target || !menuEl || !onAction) return;

    const action = target.dataset.action;
    const fieldId = Number(menuEl.dataset.fieldId);
    if (!Number.isFinite(fieldId)) return;
    if (action !== 'drone' && action !== 'truck') return;

    e.stopPropagation();
    onAction(fieldId, action);
  });

  container.appendChild(menuEl);
  return menuEl;
}

export function openFieldActionMenu(
  ctx: SceneContext,
  field: Field,
  onFieldAction: FieldActionFn,
): void {
  if (!ctx.renderer || field.dead) {
    closeFieldActionMenu();
    return;
  }

  onAction = onFieldAction;
  const el = ensureMenu(ctx.container);
  el.dataset.fieldId = String(field.id);

  const title = el.querySelector('.field-action-title');
  if (title) title.textContent = field.name;

  worldPos.set(field.x, 2.2, field.z);
  el.style.display = 'flex';
  updateFieldActionMenuPosition(ctx);
}

export function closeFieldActionMenu(): void {
  if (menuEl) {
    menuEl.style.display = 'none';
    delete menuEl.dataset.fieldId;
  }
  onAction = null;
}

export function updateFieldActionMenuPosition(ctx: SceneContext): void {
  if (!menuEl || menuEl.style.display === 'none' || !ctx.renderer) return;

  const tempV = worldPos.clone().project(ctx.camera);
  if (tempV.z > 1) {
    menuEl.style.visibility = 'hidden';
    return;
  }

  menuEl.style.visibility = 'visible';
  const rect = ctx.renderer.domElement.getBoundingClientRect();
  const x2d = (tempV.x * 0.5 + 0.5) * rect.width;
  const y2d = (tempV.y * -0.5 + 0.5) * rect.height;
  menuEl.style.left = `${x2d}px`;
  menuEl.style.top = `${y2d - 12}px`;
}

export function syncFieldActionMenu(
  ctx: SceneContext,
  state: GameState,
  onFieldAction: FieldActionFn,
): void {
  const selectedId = state.selectedFieldId;
  if (selectedId === null) {
    closeFieldActionMenu();
    return;
  }

  const field = state.fields.find((f) => f.id === selectedId);
  if (!field || field.dead) {
    closeFieldActionMenu();
    return;
  }

  if (!menuEl || menuEl.style.display === 'none' || menuEl.dataset.fieldId !== String(selectedId)) {
    openFieldActionMenu(ctx, field, onFieldAction);
  } else {
    onAction = onFieldAction;
    updateFieldActionMenuPosition(ctx);
  }
}
